import React, { useState, useEffect } from 'react';
import SearchBar from './components/SearchBar';
import WeatherCard from './components/WeatherCard';
import './styles.css';

// ============================================
// App.js - Main Component
// This is the root component of the Weather App.
// It manages the state and handles API calls.
// ============================================

// Your OpenWeatherMap API Key (get yours free at https://openweathermap.org/api)
const API_KEY = '2683b08ed6e4279c54bf51f08676674e';

function App() {
  // ---------- State Variables ----------

  // Stores the weather data fetched from API
  const [weatherData, setWeatherData] = useState(null);

  // Shows loading spinner/message while fetching
  const [loading, setLoading] = useState(false);

  // Stores error message if city not found or API fails
  const [error, setError] = useState('');

  // Tracks if location detection is in progress
  const [locationLoading, setLocationLoading] = useState(false);

  // Dark mode toggle
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('weatherApp_darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // ---------- Dark Mode Effect ----------

  useEffect(() => {
    localStorage.setItem('weatherApp_darkMode', JSON.stringify(darkMode));
    document.body.classList.toggle('dark', darkMode);
  }, [darkMode]);

  // ---------- Auto-detect Location on Load ----------

  useEffect(() => {
    detectLocation();
  // eslint-disable-next-line
  }, []);

  /**
   * Uses browser Geolocation API to detect current position
   * and fetch weather for that location
   */
  const detectLocation = () => {
    if (!navigator.geolocation) {
      return; // Geolocation not supported, silently skip
    }

    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      // Success callback
      async (position) => {
        const { latitude, longitude } = position.coords;
        await fetchWeatherByCoords(latitude, longitude);
        setLocationLoading(false);
      },
      // Error callback - user denied or error occurred
      () => {
        setLocationLoading(false);
      },
      { timeout: 10000 }
    );
  };

  /**
   * Fetches weather data using latitude & longitude
   */
  const fetchWeatherByCoords = async (lat, lon) => {
    setLoading(true);
    setError('');
    setWeatherData(null);

    try {
      const response = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric`
      );

      if (!response.ok) {
        throw new Error('Could not fetch weather for your location.');
      }

      const data = await response.json();
      setWeatherData(extractWeatherData(data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Extracts required weather data from API response
   */
  const extractWeatherData = (data) => ({
    city: data.name,
    country: data.sys.country,
    temperature: Math.round(data.main.temp),
    feelsLike: Math.round(data.main.feels_like),
    tempMin: Math.round(data.main.temp_min),
    tempMax: Math.round(data.main.temp_max),
    condition: data.weather[0].main,
    description: data.weather[0].description,
    icon: data.weather[0].icon,
    humidity: data.main.humidity,
    windSpeed: data.wind.speed,
    pressure: data.main.pressure,
    visibility: data.visibility,
  });

  // ---------- Fetch Weather Function ----------

  /**
   * Fetches weather data from OpenWeatherMap API
   * @param {string} city - The city name to search for
   */
  const fetchWeather = async (city) => {
    // Don't fetch if city is empty
    if (!city.trim()) {
      setError('Please enter a city name');
      return;
    }

    // Reset states before new search
    setLoading(true);
    setError('');
    setWeatherData(null);

    try {
      // Make API call to OpenWeatherMap
      const response = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?q=${encodeURIComponent(city)}&appid=${API_KEY}&units=metric`
      );

      // Check if city was found
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('City not found. Please check the spelling and try again.');
        } else if (response.status === 401) {
          throw new Error('Invalid API key. Please check your OpenWeatherMap API key.');
        } else {
          throw new Error('Something went wrong. Please try again later.');
        }
      }

      // Parse the JSON response
      const data = await response.json();

      // Extract only the data we need and save to state
      setWeatherData(extractWeatherData(data));
    } catch (err) {
      // Show error message to user
      setError(err.message);
    } finally {
      // Stop loading regardless of success or failure
      setLoading(false);
    }
  };

  // ---------- Background Color Based on Weather ----------

  /**
   * Returns a CSS class based on the current weather condition
   * This changes the background gradient of the app
   */
  const getBackgroundClass = () => {
    if (!weatherData) return 'bg-default';

    switch (weatherData.condition.toLowerCase()) {
      case 'clear':
        return 'bg-clear';       // Sunny yellow-orange
      case 'clouds':
        return 'bg-clouds';      // Cloudy gray
      case 'rain':
      case 'drizzle':
        return 'bg-rain';        // Rainy blue
      case 'thunderstorm':
        return 'bg-thunderstorm'; // Dark stormy
      case 'snow':
        return 'bg-snow';        // Snowy white-blue
      case 'mist':
      case 'haze':
      case 'fog':
        return 'bg-mist';        // Misty light gray
      default:
        return 'bg-default';     // Default purple gradient
    }
  };

  // ---------- Render ----------

  return (
    <div className={`app ${getBackgroundClass()} ${darkMode ? 'dark' : ''}`}>
      <div className="container">
        {/* Header with Title and Dark Mode Toggle */}
        <div className="app-header">
          <div>
            <h1 className="app-title">
              <span role="img" aria-label="sun">🌤️</span> Weather App
            </h1>
            <p className="app-subtitle">Search any city to get real-time weather</p>
          </div>
          <button className="dark-mode-toggle" onClick={() => setDarkMode(!darkMode)} title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>

        {/* Search Bar Component with Location Button */}
        <SearchBar onSearch={fetchWeather} loading={loading} onDetectLocation={detectLocation} locationLoading={locationLoading} />

        {/* Loading Spinner */}
        {loading && (
          <div className="loading">
            <div className="spinner-container">
              <div className="spinner-ring"></div>
              <div className="spinner-ring spinner-ring-2"></div>
              <div className="spinner-ring spinner-ring-3"></div>
              <div className="spinner-icon">☁️</div>
            </div>
            <p>Fetching weather data...</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span role="img" aria-label="error">⚠️</span> {error}
          </div>
        )}

        {/* Weather Card - Only shown when data is available */}
        {weatherData && !loading && (
          <WeatherCard data={weatherData} darkMode={darkMode} />
        )}

        {/* Default message when no search has been made */}
        {!weatherData && !loading && !error && (
          <div className="default-message">
            <span role="img" aria-label="search" className="default-icon">🔍</span>
            <p>Enter a city name above to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
